import React, { useEffect, useState } from 'react';

import { ShopScreen } from 'features/shop-base/ShopScreen';
import { withAuth } from 'features/auth/components/withAuth';
import useQueryParams from 'hooks/useQueryParams';
import { IS_FLIPGIVE } from 'common/env';
import { ArchivedView } from './ArchivedView';
import { CardsView } from './CardsView';
import { MainLayout } from '../components/MainLayout';
import { FlipgiveWalletIframe } from '../components/FlipgiveWalletIframe';
import { getOrdersV2 } from 'common/api/e-comm/orders/service';
import { useDispatch } from 'react-redux';
import {
    requestAffilateOffersForWalletCards,
    requestWallet,
    selectUserCards,
} from 'common/features/store/duck/wallet/duck';
import { useRootSelector } from 'rootStore';
import { useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { Value } from 'firebase/remote-config';
import { selectUserId } from 'common/features/store/duck/account/duck';
import { constructorClient } from 'third-party-integrations/constructor';
import { selectIsConstructorBrowseEnabledWeb } from 'common/features/store/duck/ffs';

const parseToMilliseconds = (n: Value) => {
    return n.asNumber() * 1000;
};

const usePollingControl = (): [boolean, React.Dispatch<React.SetStateAction<boolean>>] => {
    const [shouldStartPolling, setShouldStartPolling] = useState<boolean>(false);
    const polling_time_trigger = useFirebaseConfigValues(
        'wallet_elapsed_time_polling_trigger',
        undefined,
        parseToMilliseconds
    );

    useEffect(() => {
        if (polling_time_trigger) {
            const checkIfShouldStartPolling = async () => {
                const startDate = new Date();
                const endDate = new Date();
                endDate.setDate(endDate.getDate() + 1);

                const result = await getOrdersV2(
                    startDate.toISOString().split('T')[0],
                    endDate.toISOString().split('T')[0]
                );

                if (result.data && result.data.length > 0) {
                    const [order] = result.data;
                    const orderDate: Date = new Date(order.orderDate);

                    return startDate.getTime() - orderDate.getTime() <= polling_time_trigger;
                }

                return false;
            };
            checkIfShouldStartPolling()
                .then((shouldStartPolling: boolean) => {
                    setShouldStartPolling(shouldStartPolling);
                })
                .catch(() => {
                    console.error('failed to start wallet polling');
                });
        }
    }, [polling_time_trigger]);

    return [shouldStartPolling, setShouldStartPolling];
};

export const useRefreshWalletPolling = (): boolean => {
    const cardsById = useRootSelector(selectUserCards);
    const [shouldStartPolling, setContinuePolling] = usePollingControl();
    const userId = useRootSelector(selectUserId);
    const dispatch = useDispatch<any>();
    const showAffiliateBonuses = useRootSelector(selectIsConstructorBrowseEnabledWeb);

    const pollInterval = useFirebaseConfigValues(
        'wallet_refresh_interval',
        22000,
        parseToMilliseconds
    ) as number;
    const pollingMaxTime = useFirebaseConfigValues(
        'wallet_polling_max_time',
        5 * 60 * 1000,
        parseToMilliseconds
    ) as number;

    useEffect(() => {
        if (!showAffiliateBonuses) {
            return;
        }

        const brandIds = Object.keys(cardsById)
            .map((key) => (cardsById as any)[key]?.brandId)
            .filter((id, index, array) => index === array.indexOf(id));
        if (brandIds && brandIds.length > 0 && constructorClient) {
            dispatch(requestAffilateOffersForWalletCards(brandIds, constructorClient));
        }
    }, [cardsById]);

    useEffect(() => {
        const poll = () => {
            if (userId) {
                dispatch(requestWallet(userId));
            }
        };

        let pollingSoFar = 0;
        let timer: NodeJS.Timeout;

        if (shouldStartPolling && pollingMaxTime > 0) {
            timer = setInterval(() => {
                poll();
                pollingSoFar += pollInterval;

                if (pollingSoFar >= pollingMaxTime) {
                    clearInterval(timer);
                    setContinuePolling(false);
                    return;
                }
            }, pollInterval);
        }
        return () => {
            // NOTE: clear the timer in case user leaves page before timer gets cleared above.
            if (timer) {
                clearInterval(timer);
            }
        };
    }, [shouldStartPolling, pollingMaxTime, pollInterval, setContinuePolling, dispatch, userId]);

    return shouldStartPolling;
};

const WalletScreen = withAuth()(() => {
    const params = useQueryParams();
    const sectionParam = params.get('section');
    const isArchived = sectionParam === 'archived';
    const isFlipgiveGifts = IS_FLIPGIVE && sectionParam === 'flipgive-gifts';
    useRefreshWalletPolling();

    const section: 'archived' | 'active' | 'flipgive-gifts' = isArchived
        ? 'archived'
        : isFlipgiveGifts
        ? 'flipgive-gifts'
        : 'active';
    return (
        <ShopScreen>
            <MainLayout section={section}>
                {isFlipgiveGifts ? (
                    <FlipgiveWalletIframe />
                ) : (
                    <>
                        <ArchivedView isActive={isArchived} />
                        <CardsView isActive={!isArchived} />
                    </>
                )}
            </MainLayout>
        </ShopScreen>
    );
});

export default WalletScreen;
